import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, Button, ConfirmDialog, containerClass, cn, ErrorText } from '@dataroom/shared';
import { formatWhen, PENDING_RECOVERIES_KEY, type PendingRecovery } from '@/lib/ownership-recovery';

/**
 * Shown app-wide (in Layout) to every OWNER while a support-mediated ownership
 * recovery (ADR-0055 §4) is waiting out its 72 hours: who would become an
 * owner, when, and a Cancel button — any owner who signs in can stop it. The
 * API answers an empty list to anyone who is not an owner, so this renders
 * nothing for them (and nothing while loading or on an error).
 */
export function OwnershipRecoveryBanner() {
  const pending = useQuery({
    queryKey: PENDING_RECOVERIES_KEY,
    queryFn: () =>
      api
        .get<{ recoveries: PendingRecovery[] }>('/tenant/ownership-recoveries')
        .then((r) => r.recoveries),
    retry: false,
  });
  if (!pending.data || pending.data.length === 0) return null;
  return (
    <div className="border-b bg-danger-soft text-danger" role="alert">
      {pending.data.map((r) => (
        <RecoveryNotice key={r.id} recovery={r} />
      ))}
    </div>
  );
}

function RecoveryNotice({ recovery }: { recovery: PendingRecovery }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const cancel = useMutation({
    mutationFn: () => api.post(`/tenant/ownership-recoveries/${recovery.id}/cancel`),
    onSuccess: () => {
      setConfirming(false);
      void qc.invalidateQueries({ queryKey: PENDING_RECOVERIES_KEY });
    },
  });
  return (
    <div
      className={cn(
        containerClass,
        'flex flex-wrap items-center justify-between gap-3 py-2 text-sm',
      )}
    >
      <span>
        ClosingVault support is making <strong>{recovery.assigneeEmail}</strong> an owner of this
        account. It takes effect on {formatWhen(recovery.effectiveAt)} unless an owner cancels it.
      </span>
      <Button size="sm" variant="destructive" onClick={() => setConfirming(true)}>
        Cancel this recovery
      </Button>
      <ErrorText error={cancel.error} className="w-full" />
      {confirming ? (
        <ConfirmDialog
          title="Cancel the ownership recovery?"
          confirmLabel="Cancel the recovery"
          cancelLabel="Keep it"
          danger
          pending={cancel.isPending}
          onConfirm={() => cancel.mutate()}
          onCancel={() => setConfirming(false)}
        >
          <p className="text-sm">
            {recovery.assigneeEmail} will not become an owner. Every owner and the recovery contact
            are told. If you did not expect this request, also contact ClosingVault support.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
